import { Component, computed, inject, input, model, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { fromWire, toWire } from '@core/filters/path-filter/codec';
import { DraftGroup } from '@core/filters/path-filter/draft';
import {
  TEMPLATE_LABEL_MAX_LENGTH,
  TemplateIssue,
  holdsHoles,
  templateIssues,
  templateMismatchOf,
  templateParameters,
  withoutTemplateHoles
} from '@core/filters/path-filter/templates';
import {
  FilterVocabulary,
  decodeOptionsFor
} from '@core/filters/path-filter/vocabulary/vocabulary';
import { FilterPresetScope } from '@core/models/filter-preset.model';
import { GbeError } from '@core/models/gbe/gbe-error.model';
import { ICONS } from '@core/services/icons/icons.service';
import { isNil } from '@core/utils/shared-utils';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import {
  NewParameter,
  NewParameterPromptComponent
} from '@shared/components/path-filter/new-parameter-prompt/new-parameter-prompt.component';
import { NewParameterRequest } from '@shared/components/path-filter/path-filter.models';

import { FilterPresetsStore } from '../filter-presets.store';

/** Same bound as a preset name. */
const NAME_MAX_LENGTH = 80;

/** Shown by the disabled save button rather than as a message on a row nobody has typed in yet. */
const SILENT_ROW_CODES: ReadonlySet<string> = new Set(['missing-label']);

@Component({
  selector: 'app-filter-template-author',
  imports: [TranslatePipe, CheckboxComponent, NewParameterPromptComponent],
  templateUrl: './filter-template-author.component.html',
  host: { class: 'flex flex-col' },
  styles: ``
})
export class FilterTemplateAuthorComponent {
  protected readonly presetsStore = inject(FilterPresetsStore);
  private readonly translate = inject(TranslateService);

  readonly draft = model.required<DraftGroup>();
  readonly vocabulary = input.required<FilterVocabulary>();

  protected readonly ICONS = ICONS;
  protected readonly NAME_MAX_LENGTH = NAME_MAX_LENGTH;
  protected readonly LABEL_MAX_LENGTH = TEMPLATE_LABEL_MAX_LENGTH;

  protected readonly name = signal('');
  protected readonly shareWithDataspace = signal(false);
  protected readonly includeSort = signal(false);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly serverIssues = signal<TemplateIssue[]>([]);

  private readonly tree = computed(() => toWire(this.draft()));

  protected readonly parameters = computed(() =>
    templateParameters(this.tree(), this.presetsStore.templateLabels())
  );

  protected readonly parameterNames = computed(() =>
    this.parameters().map((parameter) => parameter.name)
  );

  private readonly issues = computed(() => templateIssues(this.tree(), this.parameters()));

  protected readonly rowIssues = computed(() => {
    const rows = new Set(this.parameterNames());
    const byName = new Map<string, TemplateIssue[]>();
    for (const issue of [...this.issues(), ...this.serverIssues()]) {
      if (isNil(issue.name) || !rows.has(issue.name) || SILENT_ROW_CODES.has(issue.code))
        continue;
      byName.set(issue.name, [...(byName.get(issue.name) ?? []), issue]);
    }
    return byName;
  });

  /** Issues no row can carry: a filter-wide one, or a name gbe reported that the filter lacks. */
  protected readonly formIssues = computed(() => {
    const rows = new Set(this.parameterNames());
    return [...this.issues(), ...this.serverIssues()].filter(
      (issue) =>
        (isNil(issue.name) || !rows.has(issue.name)) && issue.code !== 'no-parameters'
    );
  });

  protected readonly canSave = computed(() => {
    const name = this.name().trim();
    return (
      !this.saving() &&
      name.length > 0 &&
      name.length <= NAME_MAX_LENGTH &&
      this.issues().length === 0
    );
  });

  protected readonly setShare = (checked: boolean): void =>
    this.shareWithDataspace.set(checked);
  protected readonly setIncludeSort = (checked: boolean): void =>
    this.includeSort.set(checked);

  protected labelOf(name: string): string {
    return this.presetsStore.templateLabels()[name] ?? '';
  }

  protected setLabel(name: string, label: string): void {
    this.presetsStore.templateLabels.update((labels) => ({ ...labels, [name]: label }));
    this.serverIssues.set([]);
  }

  /**
   * Names the parameter the slot asked for; resolving the request writes `{{name}}` into that slot.
   * The request is cleared first: what `resolve` sets off must not find it still pending.
   */
  protected createParameter(request: NewParameterRequest, created: NewParameter): void {
    this.setLabel(created.name, created.label);
    this.presetsStore.templateParameterRequest.set(null);
    request.resolve(created.name);
  }

  protected cancelNewParameter(): void {
    this.presetsStore.templateParameterRequest.set(null);
  }

  protected save(): void {
    const tree = this.tree();
    if (!this.canSave() || isNil(tree)) {
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    this.serverIssues.set([]);
    const scope = this.shareWithDataspace()
      ? FilterPresetScope.DATASPACE
      : FilterPresetScope.USER;

    this.presetsStore
      .saveTemplate(this.name().trim(), scope, tree, this.parameters(), this.includeSort())
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.leave();
        },
        error: (err: unknown) => {
          this.saving.set(false);
          const mismatch = templateMismatchOf(err);
          if (isNil(mismatch)) {
            this.error.set(
              (err as Partial<GbeError> | null)?.errorMessage ??
                this.translate.instant(
                  'features.data.filter_presets.template.generic_error'
                )
            );
            return;
          }
          this.serverIssues.set([
            ...mismatch.undeclared.map((name) => ({ code: 'undeclared' as const, name })),
            ...mismatch.unused.map((name) => ({ code: 'unused' as const, name }))
          ]);
        }
      });
  }

  protected discard(): void {
    this.leave();
  }

  /**
   * Back to a plain filter: a `{{name}}` left in it would be sent as a literal value. A filter
   * without holes is left alone, since any rewrite of the bar counts as a hand edit and detaches
   * the active preset.
   */
  private leave(): void {
    const tree = this.tree();
    if (!isNil(tree) && holdsHoles(tree)) {
      this.draft.set(
        fromWire(withoutTemplateHoles(tree), decodeOptionsFor(this.vocabulary()))
      );
    }
    this.presetsStore.endTemplateAuthoring();
    this.name.set('');
    this.includeSort.set(false);
    this.serverIssues.set([]);
    this.error.set(null);
  }
}
